import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import {
  SelectContainer,
  SelectLabel,
  SelectButton,
  SelectIcon,
  DropdownList,
  DropdownOption,
  ErrorMessage,
  DisplayText
} from './styled'
import { ChevronDown, ErrorIcon } from '../../../assets/icons'

const Select = ({
  width,
  label,
  name,
  value,
  onChange,
  options = [],
  placeholder = 'Seleccionar...',
  error,
  disabled = false,
  required = false,
  isClearable = false,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef(null)

  const selectedOption = options.find(option => option.value === value)
  const displayText = selectedOption ? selectedOption.label : placeholder

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false)
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen])

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen)
    }
  }

  const handleSelect = (option) => {
    const syntheticEvent = {
      target: {
        name: name,
        value: option.value
      }
    }
    onChange(syntheticEvent)
    setIsOpen(false)
  }

  const handleClear = (e) => {
    e.stopPropagation()
    const syntheticEvent = {
      target: {
        name,
        value: null
      }
    }

    onChange(syntheticEvent)
    setIsOpen(false)
  }

  return (
    <div ref={containerRef} style={{ width: width || '100%' }}>
      <SelectContainer>
        {label && (
          <SelectLabel htmlFor={name}>
            {label}
            {required && <span style={{ color: '#D32F2F' }}> *</span>}
          </SelectLabel>
        )}

        <div style={{ position: 'relative' }}>
          <SelectButton
            type="button"
            id={name}
            onClick={handleToggle}
            disabled={disabled}
            $error={!!error}
            $isOpen={isOpen}
            $hasValue={!!value}
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            {...props}
          >
            <DisplayText>{displayText}</DisplayText>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '8px' }}>
              {value && value !== '' && !disabled && isClearable && (
                <button
                  type="button"
                  onClick={handleClear}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: '2px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 0.6,
                    transition: 'opacity 0.2s',
                    pointerEvents: 'auto'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = '0.6'}
                  aria-label="Limpiar selección"
                >
                  ✕
                </button>
              )}
              <SelectIcon $isOpen={isOpen}>
                <ChevronDown />
              </SelectIcon>
            </div>
          </SelectButton>

          {isOpen && (
            <DropdownList>
              {options.map((option) => (
                <DropdownOption
                  key={option.value}
                  onClick={() => handleSelect(option)}
                  $isSelected={option.value === value}
                >
                  {option.label}
                </DropdownOption>
              ))}
            </DropdownList>
          )}
        </div>

        {error && <ErrorMessage>{error}</ErrorMessage>}
      </SelectContainer>
    </div>
  )
}

Select.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired,
  placeholder: PropTypes.string,
  error: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  isClearable: PropTypes.bool
}

export default Select